import { useFormatDateTime } from '../../hooks/useFormatDate.js';

/**
 * Build/version stamp shown at the bottom of the sidebar. Answers "am I on the
 * latest deploy?" at a glance — it prints the commit short-SHA, the commit
 * title, and the build date baked in at build time (vite.config.ts). Because
 * the web app is a PWA that can serve a cached build, this is the reliable way
 * to confirm what's actually running versus what was last pushed.
 *
 * It is developer metadata, so 12px (`text-xs`) is the right size for it — but
 * it is not exempt from the floor either. It shipped at 11px.
 *
 * It sits at the foot of the nav rail, so its ink comes from
 * `--nav-on-surface` — the token paired with the rail's own `--nav-surface` —
 * not from `--muted-foreground`/`--foreground`, which are inks for a neutral
 * surface and measure 1.85:1 and 1.00:1 against the rail in light theme (the
 * rail IS `--foreground`'s colour there).
 *
 * It used to draw from `--primary-foreground`, back when the rail was a slab of
 * `--primary`. The nav split off its own tokens and this was left behind, which
 * nothing caught because in light `--primary-foreground` is white and so is
 * `--nav-on-surface` — identical output, passing by coincidence. In dark
 * `--primary-foreground` flips to #2f009b while the rail stays ink by design,
 * and the stamp went to 1.46:1 / 1.27:1. Measured over the rail:
 *
 *                          light            dark
 *   commit SHA (full)      17.16:1        19.29:1   (was 17.16 / 1.46)
 *   date + message `/75`   10.05:1        10.91:1   (was 10.05 / 1.27)
 *
 * The `/15` top border is a decorative hairline, like `--border` itself, and is
 * not held to a contrast floor.
 */
/**
 * The build stamp in the tenant's own date + clock, like every other stamp in
 * the product. It was `toLocaleString(undefined, …)` — the DEVICE's date order
 * and hour cycle — so the rail's footer printed a 12-hour time under a 24-hour
 * screen. The raw ISO survives an unparseable value: a build stamp we cannot
 * read is worth showing verbatim to whoever is debugging it.
 */
function useBuildDate(): (iso: string) => string {
  const formatDateTime = useFormatDateTime();
  return (iso) => formatDateTime(iso) || iso;
}

export function AppVersion(): JSX.Element {
  const formatBuildDate = useBuildDate();
  const commit = __BUILD_COMMIT__;
  const message = __BUILD_MESSAGE__;
  const date = formatBuildDate(__BUILD_DATE__);

  return (
    <div
      className="shrink-0 border-t border-nav-border px-4 py-2 text-xs leading-tight text-nav-on-surface-muted"
      title={`Commit ${commit}: ${message}\nBuilt ${date}`}
    >
      {/* THE SHA AND THE DATE ARE SHOWN; THE COMMIT SUBJECT IS NOT.
          This used to render the message as a second line, so every site admin
          and HR user had "fix(employees): prose out of the definition grid" in
          their navigation — a sentence written for this repository, addressed
          to nobody using the product.
          The stamp itself stays, because the need behind it is real and
          operational: the web app is a PWA that can serve a cached build, and
          asking someone to read four characters back is how support tells "you
          are on the old bundle" from "the fix did not work". That need is met
          by the SHA and the date. The message is kept in the `title` above, so
          it is one hover away for whoever actually wants it. */}
      <p className="flex items-center gap-control">
        <span className="font-mono font-medium text-nav-on-surface">{commit}</span>
        <span aria-hidden>·</span>
        <span>{date}</span>
      </p>
    </div>
  );
}
